// CountElementsFromBaseTask.js
// Zadanie 1: Dany ostrosłup o podstawie będącej n-kątem. Oblicz liczbę (Ścian / Krawędzi / Wierzchołków).
// Oznaczenia (spolszczone): s – ściany, k – krawędzie, w – wierzchołki
// Zależności: s = n + 1, w = n + 1, k = 2n

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function nameOfNGon(n) {
  const map = {
    3: "trójkątem",
    4: "czworokątem",
    5: "pięciokątem",
    6: "sześciokątem",
    7: "siedmiokątem",
    8: "ośmiokątem",
    9: "dziewięciokątem",
    10: "dziesięciokątem",
  };
  return map[n] || `${n}-kątem`;
}

function parseIntAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return null;
  const s = String(userAnswer.raw).trim();
  if (!/^-?\d+$/.test(s)) return null;
  return parseInt(s, 10);
}

function renderMathNumber(value) {
  const s = String(value);
  return <InlineMath math={'{' + s + '}'} />;
}

export function generateTask() {
  const n = pick([3, 4, 5, 6, 7, 8, 9, 10]);
  const want = pick(["faces", "edges", "vertices"]); // ściany / krawędzie / wierzchołki

  let correct;
  if (want === "faces") correct = n + 1; // s
  else if (want === "vertices") correct = n + 1; // w
  else correct = 2 * n; // k

  const wantLabel =
    want === "faces" ? "ścian" : want === "edges" ? "krawędzi" : "wierzchołków";

  return {
    type: "CountElementsFromBaseTask",
    data: { n, want, wantLabel, correct },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: 12 }}>
      Ostrosłup ma w podstawie wielokąt będący <b>{nameOfNGon(data.n)}</b>. Ile ma{" "}
      {data.wantLabel}?
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const handle = (ans) => onChange?.(ans);
  return (
    <FractionInput
      value={userAnswer}
      onChange={handle}
      disabled={disabled}
      placeholder="np. 12"
      label=""
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ans = parseIntAnswer(userAnswer);
  if (ans === null) return false;
  return ans === data.correct;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;

  const parsed = parseIntAnswer(userAnswer);
  if (parsed !== null) return renderMathNumber(parsed);

  return <span>{String(userAnswer.raw)}</span>;
}

export function renderAnswer({ data }) {
  return renderMathNumber(data.correct);
}

export function renderExplanation({ data }) {
  const { n, want } = data;

  const general = "s=n+1\\quad w=n+1\\quad k=2n";

  if (want === "faces") {
    // s
    return (
      <div style={{ display: "grid", rowGap: 8 }}>
        <p>
          <strong>Oznaczenia:</strong> <InlineMath math={'{s}'} /> – liczba ścian,{" "}
          <InlineMath math={'{w}'} /> – liczba wierzchołków,{" "}
          <InlineMath math={'{k}'} /> – liczba krawędzi.
        </p>

        <p><strong>Krok 1:</strong> Zależności ogólne:</p>
        <BlockMath math={general} />

        <p><strong>Krok 2:</strong> Dla ścian mamy <InlineMath math={'{s=n+1}'} />.</p>
        <p><strong>Krok 3:</strong> Podstawiamy <InlineMath math={'{n=' + n + '}'} />:</p>
        <BlockMath math={'{s=' + n + '+1=' + (n + 1) + '}'} />
      </div>
    );
  }

  if (want === "vertices") {
    // w
    return (
      <div style={{ display: "grid", rowGap: 8 }}>
        <p>
          <strong>Oznaczenia:</strong> <InlineMath math={'{s}'} /> – liczba ścian,{" "}
          <InlineMath math={'{w}'} /> – liczba wierzchołków,{" "}
          <InlineMath math={'{k}'} /> – liczba krawędzi.
        </p>

        <p><strong>Krok 1:</strong> Zależności ogólne:</p>
        <BlockMath math={general} />

        <p><strong>Krok 2:</strong> Dla wierzchołków mamy <InlineMath math={'{w=n+1}'} />.</p>
        <p><strong>Krok 3:</strong> Podstawiamy <InlineMath math={'{n=' + n + '}'} />:</p>
        <BlockMath math={'{w=' + n + '+1=' + (n + 1) + '}'} />
      </div>
    );
  }

  // edges => k
  return (
    <div style={{ display: "grid", rowGap: 8 }}>
      <p>
        <strong>Oznaczenia:</strong> <InlineMath math={'{s}'} /> – liczba ścian,{" "}
        <InlineMath math={'{w}'} /> – liczba wierzchołków,{" "}
        <InlineMath math={'{k}'} /> – liczba krawędzi.
      </p>

      <p><strong>Krok 1:</strong> Zależności ogólne:</p>
      <BlockMath math={general} />

      <p><strong>Krok 2:</strong> Dla krawędzi mamy <InlineMath math={'{k=2n}'} />.</p>
      <p><strong>Krok 3:</strong> Podstawiamy <InlineMath math={'{n=' + n + '}'} />:</p>
      <BlockMath math={'{k=2\\cdot ' + n + '=' + 2 * n + '}'} />
    </div>
  );
}
